import { useQuery } from '@tanstack/react-query';
import { executeReportQuery, clearReportCache } from '@/lib/reportQueryBuilder';
import { executeDrillDownQuery } from '@/lib/reportQueryBuilder';
import type { ReportConfig, ReportQueryResult, DrillDownContext } from '@/types/custom-reports';
import type { Transaction } from '@/types';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const reportQueryKeys = {
  all: ['report-query'] as const,
  results: () => [...reportQueryKeys.all, 'result'] as const,
  result: (config: ReportConfig) => [...reportQueryKeys.results(), config] as const,
  drillDown: (context: DrillDownContext) => ['drill-down', context] as const,
};

// ============================================================================
// HOOKS FOR REPORT QUERIES
// ============================================================================

/**
 * Execute a report query based on configuration
 * Uses React Query for caching and refetching
 */
export function useReportQuery(config: ReportConfig | null, options?: {
  enabled?: boolean;
  staleTime?: number;
}) {
  // Generate a stable key from config
  const configKey = config ? JSON.stringify(config) : 'empty';

  return useQuery({
    queryKey: [...reportQueryKeys.results(), configKey],
    queryFn: () => config ? executeReportQuery(config) : null,
    enabled: options?.enabled !== false && !!config && config.dataSources?.length > 0,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutes default
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Execute a drill-down query to show transactions for a specific group
 */
export function useDrillDownQuery(context: DrillDownContext | null, options?: {
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: context ? reportQueryKeys.drillDown(context) : ['drill-down', null],
    queryFn: () => context ? executeDrillDownQuery(context) : [],
    enabled: options?.enabled !== false && context !== null,
  });
}

/**
 * Hook to manually clear report cache
 * Call this after creating/updating/deleting transactions
 */
export function useClearReportCache() {
  return () => clearReportCache();
}

/**
 * Hook to prefetch a report query (for optimistic loading)
 */
export function usePrefetchReportQuery() {
  const { prefetchQuery } = useQuery(); // Note: This needs to be from QueryClient

  return (config: ReportConfig) => {
    prefetchQuery({
      queryKey: reportQueryKeys.result(config),
      queryFn: () => executeReportQuery(config),
      staleTime: 5 * 60 * 1000,
    });
  };
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Check if a report query is loading
 */
export function useReportQueryLoading(config: ReportConfig | null) {
  const query = useReportQuery(config || {} as ReportConfig, {
    enabled: false, // Don't auto-fetch
  });

  return query.isLoading || query.isFetching;
}

/**
 * Get error state for a report query
 */
export function useReportQueryError(config: ReportConfig | null) {
  const query = useReportQuery(config || {} as ReportConfig, {
    enabled: false, // Don't auto-fetch
  });

  return query.error;
}
